import apiClient from './apiClient';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import logger from '../utils/logger';

// API Endpoints - minimal API
const CREDITS_API_BASE = '/api/credits';
const CREDIT_CONSUMPTION_API_BASE = '/api/creditconsumption';

const GET_CREDITS_STATUS_API = `${CREDITS_API_BASE}/getCreditStatus`;
const PURCHASE_CREDITS_API = `${CREDITS_API_BASE}/purchase`;
const CONSUME_CREDIT_API = `${CREDITS_API_BASE}/consume`;
const CREDIT_USAGE_HISTORY_API = `${CREDIT_CONSUMPTION_API_BASE}/usage-history`;

/**
 * Serviciu simplificat pentru gestionarea creditelor
 *
 * Doar 2 funcții esențiale:
 * 1. getCreditsStatus - Interogare situație credite
 * 2. purchaseCredits - Cumpărare credite extra
 */

/**
 * Obține statusul complet al creditelor utilizatorului
 * @returns {Promise<{
 *   success: boolean,
 *   availableCredits: number,
 *   extraCredits: number,
 *   totalCredits: number,
 *   canUseCredits: boolean,
 *   message: string,
 *   errorDetails?: string
 * }>}
 */
export const getCreditsStatus = async () => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        availableCredits: 0,
        extraCredits: 0,
        totalCredits: 0,
        canUseCredits: false,
        message: 'Trebuie să te autentifici pentru a verifica creditele.',
      };
    }

    const response = await apiClient.get(GET_CREDITS_STATUS_API);

    const data = response.data;
    return {
      success: data.success ?? true,
      availableCredits: data.availableCredits ?? 0,
      extraCredits: data.extraCredits ?? 0,
      totalCredits: data.totalCredits ?? 0,
      canUseCredits: data.canUseCredits ?? false,
      subscriptionType: data.subscriptionType,
      pricePerCredit: data.pricePerCredit, // Prețul per credit din abonament
      message: data.message || 'Status credite obținut cu succes.',
      errorDetails: data.errorDetails,
    };
  } catch (error) {
    logger.error('Error getting credits status:', error);

    return {
      success: false,
      availableCredits: 0,
      extraCredits: 0,
      totalCredits: 0,
      canUseCredits: false,
      message:
        'Eroare la obținerea statusului creditelor. Te rugăm să încerci din nou.',
    };
  }
};

/**
 * Cumpără credite extra cu actualizare automată JWT
 * @param {number} quantity - Numărul de credite de cumpărat
 * @returns {Promise<{
 *   success: boolean,
 *   transactionId?: string,
 *   quantityPurchased?: number,
 *   pricePerCredit?: number,
 *   baseAmount?: number,
 *   discountPercent?: number,
 *   finalAmount?: number,
 *   oldExtraCredits?: number,
 *   newExtraCreditsTotal?: number,
 *   totalCreditsAfterPurchase?: number,
 *   updatedJWT?: string,
 *   message: string,
 *   errorDetails?: string
 * }>}
 */
export const purchaseCredits = async (quantity) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        message: 'Trebuie să te autentifici pentru a cumpăra credite.',
      };
    }

    if (!quantity || quantity <= 0) {
      return {
        success: false,
        message: 'Cantitatea trebuie să fie mai mare decât 0.',
      };
    }

    const requestData = {
      quantity: parseInt(quantity),
      paymentMethod: 'stripe', // Stripe va gestiona plata
      acceptTerms: true,
    };

    const response = await apiClient.post(PURCHASE_CREDITS_API, requestData);

    const data = response.data;

    // Actualizează JWT-ul în cookies dacă backend-ul returnează unul nou
    if (data.success && data.updatedJWT) {
      Cookies.set('jwtToken', data.updatedJWT, { expires: 7 }); // 7 zile
      logger.log('JWT actualizat cu noile credite:', {
        oldExtra: data.oldExtraCredits,
        newExtra: data.newExtraCreditsTotal,
        total: data.totalCreditsAfterPurchase,
      });
    }

    return {
      success: data.success ?? false,
      transactionId: data.transactionId,
      paymentProcessor: data.paymentProcessor,
      quantityPurchased: data.quantityPurchased,
      pricePerCredit: data.pricePerCredit,
      baseAmount: data.baseAmount,
      discountPercent: data.discountPercent,
      discountAmount: data.discountAmount,
      finalAmount: data.finalAmount,
      currency: data.currency,
      oldExtraCredits: data.oldExtraCredits,
      newExtraCreditsTotal: data.newExtraCreditsTotal,
      totalCreditsAfterPurchase: data.totalCreditsAfterPurchase,
      purchaseDate: data.purchaseDate,
      updatedJWT: data.updatedJWT,
      message: data.message || 'Creditele au fost achiziționate cu succes.',
      errorDetails: data.errorDetails,
    };
  } catch (error) {
    logger.error('Error purchasing credits:', error);

    return {
      success: false,
      message: 'Eroare la achiziția creditelor. Te rugăm să încerci din nou.',
    };
  }
};

/**
 * Calculează prețul pentru credite extra
 * @param {number} quantity - Numărul de credite
 * @returns {{
 *   quantity: number,
 *   pricePerCredit: number,
 *   totalPrice: number,
 *   currency: string
 * }}
 */
export const calculateCreditsPrice = (quantity) => {
  const pricePerCredit = 490; // RON
  return {
    quantity: parseInt(quantity) || 0,
    pricePerCredit,
    totalPrice: (parseInt(quantity) || 0) * pricePerCredit,
    currency: 'RON',
  };
};

/**
 * Verifică din JWT dacă utilizatorul poate genera oferte (folosit în GenerateOfferButton)
 * @returns {boolean}
 */
export const canGenerateOfferFromJWT = () => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) return false;

    const decoded = jwtDecode(token);
    const availableCredits = parseInt(decoded.availableCredits) || 0;
    const extraCredits = parseInt(decoded.extraCredits) || 0;

    return availableCredits + extraCredits > 0;
  } catch (error) {
    logger.error('Error checking JWT credits:', error);
    return false;
  }
};

/**
 * Verifică drepturile de generare a ofertei pentru GenerateOfferButton
 * @returns {Promise<{
 *   canGenerate: boolean,
 *   availableCredits: number,
 *   extraCredits: number,
 *   totalCredits: number,
 *   message: string
 * }>}
 */
export const checkOfferGenerationRights = async () => {
  try {
    // Apelează direct backend-ul pentru informații actualizate despre credite
    const status = await getCreditsStatus();

    return {
      canGenerate: status.canUseCredits,
      availableCredits: status.availableCredits,
      extraCredits: status.extraCredits,
      totalCredits: status.totalCredits,
      message: status.message,
    };
  } catch (error) {
    logger.error('Error checking offer generation rights:', error);
    return {
      canGenerate: false,
      availableCredits: 0,
      extraCredits: 0,
      totalCredits: 0,
      message: 'Eroare la verificarea creditelor. Te rugăm să încerci din nou.',
    };
  }
};

/**
 * Obține istoricul tranzacțiilor cu credite
 * @param {number} page - Pagina curentă (implicit 1)
 * @param {number} pageSize - Numărul de tranzacții per pagină (implicit 20)
 * @returns {Promise<{
 *   success: boolean,
 *   page: number,
 *   pageSize: number,
 *   totalTransactions: number,
 *   totalPages: number,
 *   transactions: Array,
 *   message: string
 * }>}
 */
export const getCreditsHistory = async (page = 1, pageSize = 20) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        page: page,
        pageSize: pageSize,
        totalTransactions: 0,
        totalPages: 0,
        transactions: [],
        message: 'Trebuie să te autentifici pentru a vedea istoricul.',
      };
    }

    const response = await apiClient.get(`${CREDITS_API_BASE}/history`, {
      params: { page, pageSize },
    });

    const data = response.data;
    return {
      success: data.success ?? false,
      page: data.page ?? page,
      pageSize: data.pageSize ?? pageSize,
      totalTransactions: data.totalTransactions ?? 0,
      totalPages: data.totalPages ?? 0,
      transactions: data.transactions ?? [],
      message: data.message || 'Istoric obținut cu succes.',
    };
  } catch (error) {
    logger.error('Error getting credits history:', error);
    return {
      success: false,
      page: page,
      pageSize: pageSize,
      totalTransactions: 0,
      totalPages: 0,
      transactions: [],
      message: 'Eroare la obținerea istoricului. Te rugăm să încerci din nou.',
    };
  }
};

/**
 * Confirmă plata după întoarcerea de la Stripe și actualizează creditele
 * @param {string} sessionId - ID-ul sesiunii Stripe
 * @returns {Promise<{
 *   success: boolean,
 *   availableCredits?: number,
 *   extraCredits?: number,
 *   totalCredits?: number,
 *   message: string,
 *   errorDetails?: string
 * }>}
 */
export const confirmPayment = async (sessionId) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        message: 'Trebuie să te autentifici pentru a confirma plata.',
      };
    }

    if (!sessionId) {
      return {
        success: false,
        message: 'SessionId este necesar pentru confirmarea plății.',
      };
    }

    const requestData = {
      sessionId: sessionId,
    };

    const response = await apiClient.post(
      `${CREDITS_API_BASE}/confirm-payment`,
      requestData,
    );

    const responseData = response.data;

    return {
      success: responseData.success ?? false,
      availableCredits: responseData.availableCredits,
      extraCredits: responseData.extraCredits,
      totalCredits: responseData.totalCredits,
      message: responseData.message || 'Plata confirmată cu succes.',
      errorDetails: responseData.errorDetails,
    };
  } catch (error) {
    logger.error('Error confirming payment:', error);

    if (error.response && error.response.data) {
      return {
        success: false,
        message: error.response.data.message || 'Eroare la confirmarea plății.',
        errorDetails: error.response.data.errorDetails,
      };
    }

    return {
      success: false,
      message:
        'Eroare neașteptată la confirmarea plății. Te rugăm să încerci din nou.',
    };
  }
};
export const createOneTimePayment = async (data) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        message: 'Trebuie să te autentifici pentru a cumpăra credite.',
      };
    }

    if (!data.quantity || data.quantity <= 0) {
      return {
        success: false,
        message: 'Cantitatea trebuie să fie mai mare decât 0.',
      };
    }

    if (!data.acceptTerms) {
      return {
        success: false,
        message: 'Trebuie să accepți termenii și condițiile pentru a continua.',
      };
    }

    const requestData = {
      Quantity: parseInt(data.quantity),
      Currency: data.currency || 'RON',
      AcceptTerms: data.acceptTerms,
    };

    // Optional pack-purchase unit. Backend treats Unit === "pack" as the pack model
    // (1 unit = 500 credits @ 490 RON). Omitting it preserves the legacy per-credit
    // semantics, so older callers continue working unchanged.
    if (data.unit) {
      requestData.Unit = data.unit;
    }

    const response = await apiClient.post(PURCHASE_CREDITS_API, requestData);

    const responseData = response.data;

    return {
      success: responseData.success ?? false,
      url: responseData.url,
      sessionId: responseData.sessionId,
      message: responseData.message || 'Sesiune de plată creată cu succes.',
      errorDetails: responseData.errorDetails,
    };
  } catch (error) {
    logger.error('Error creating payment session:', error);

    if (error.response && error.response.data) {
      return {
        success: false,
        message:
          error.response.data.message || 'Eroare la crearea sesiunii de plată.',
        errorDetails: error.response.data.errorDetails,
      };
    }

    return {
      success: false,
      message:
        'Eroare neașteptată la crearea sesiunii de plată. Te rugăm să încerci din nou.',
    };
  }
};

/**
 * Consumă un credit pentru o acțiune specifică
 * @param {string} actionType - Tipul acțiunii (ex: "AI_REPORT_GENERATION")
 * @param {string} resourceId - ID-ul resursei (opțional)
 * @returns {Promise<{
 *   success: boolean,
 *   message: string,
 *   creditSource?: string,
 *   remainingCredits?: number,
 *   usageId?: string,
 *   errorDetails?: string
 * }>}
 */
export const consumeCredit = async (actionType, resourceId = null) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        message: 'Trebuie să te autentifici pentru a utiliza creditele.',
      };
    }

    if (!actionType) {
      return {
        success: false,
        message: 'Tipul acțiunii este necesar.',
      };
    }

    const requestData = {
      actionType: actionType,
      resourceId: resourceId,
    };

    const response = await apiClient.post(CONSUME_CREDIT_API, requestData);

    const responseData = response.data;

    return {
      success: responseData.success ?? false,
      message: responseData.message || 'Credit consumat cu succes.',
      creditSource: responseData.creditSource,
      remainingCredits: responseData.remainingCredits,
      usageId: responseData.usageId,
      errorDetails: responseData.errorDetails,
    };
  } catch (error) {
    logger.error('Error consuming credit:', error);

    if (error.response && error.response.data) {
      return {
        success: false,
        message:
          error.response.data.message || 'Eroare la consumarea creditului.',
        errorDetails: error.response.data.errorDetails,
      };
    }

    return {
      success: false,
      message:
        'Eroare neașteptată la consumarea creditului. Te rugăm să încerci din nou.',
    };
  }
};

/**
 * Obține istoricul de utilizare a creditelor
 * @param {number} page - Pagina curentă (implicit 1)
 * @param {number} pageSize - Numărul de înregistrări per pagină (implicit 20)
 * @returns {Promise<{
 *   success: boolean,
 *   page: number,
 *   pageSize: number,
 *   totalRecords: number,
 *   usageHistory: Array,
 *   message: string
 * }>}
 */
export const getCreditUsageHistory = async (page = 1, pageSize = 20) => {
  try {
    const token = Cookies.get('jwtToken');
    if (!token) {
      return {
        success: false,
        page: page,
        pageSize: pageSize,
        totalRecords: 0,
        usageHistory: [],
        message:
          'Trebuie să te autentifici pentru a vedea istoricul utilizării.',
      };
    }

    const response = await apiClient.get(CREDIT_USAGE_HISTORY_API, {
      params: { page, pageSize },
    });

    const data = response.data;
    return {
      success: data.success ?? false,
      page: data.page ?? page,
      pageSize: data.pageSize ?? pageSize,
      totalRecords: data.totalRecords ?? 0,
      usageHistory: data.usageHistory ?? [],
      message: data.message || 'Istoric utilizare obținut cu succes.',
    };
  } catch (error) {
    logger.error('Error getting credit usage history:', error);
    return {
      success: false,
      page: page,
      pageSize: pageSize,
      totalRecords: 0,
      usageHistory: [],
      message:
        'Eroare la obținerea istoricului utilizării. Te rugăm să încerci din nou.',
    };
  }
};
